import { memo, useCallback, useState } from 'react';
import { motion } from 'motion/react';

import { AgentOptionItem, FeedbackType, GeneratedFile, ThinkingStep } from '@Models';
import { AGENT_DURATIONS, AGENT_EASING } from '../../../Helpers/Agents/animations';
import { extractFileMarkers } from '../../../Helpers/Agents/fileMarkers';
import FileDownloadButton from '../../FileDownloadButton/FileDownloadButton';
import ChoiceGrid from './ChoiceGrid';
import ImageAttachment from './ImageAttachment';
import ImageLightbox from './ImageLightbox';
import MarkdownMessage from './MarkdownMessage';
import MessageActions from './MessageActions';
import MultiSelectCards from './MultiSelectCards';
import OptionCards from './OptionCards';
import ThinkingSummary from './ThinkingSummary';

interface MessageAgentProps {
  content: string;
  timestamp: Date;
  conversationId: string;
  interactionTimestamp: string;
  feedback?: FeedbackType;
  isLatest?: boolean;
  options?: AgentOptionItem[];
  selectedOptionId?: string;
  generatedFiles?: GeneratedFile[];
  isStreaming?: boolean;
  optionsDisabled?: boolean;
  multiselectOptions?: AgentOptionItem[];
  selectedOptionIds?: string[];
  choicegridOptions?: AgentOptionItem[];
  choicegridAnswers?: Record<string, string>;
  thinkingSteps?: ThinkingStep[];
  thinkingSeconds?: number | null;
  onSelectOption?: (option: AgentOptionItem) => void;
  onConfirmSelection?: (selected: AgentOptionItem[]) => void;
  onConfirmChoices?: (answered: { row: AgentOptionItem; choice: { id: string; label: string } }[]) => void;
  onRegenerate?: () => void;
}

const MessageAgent = ({
  content,
  timestamp,
  conversationId,
  interactionTimestamp,
  feedback,
  isLatest = false,
  options,
  selectedOptionId,
  generatedFiles,
  isStreaming = false,
  optionsDisabled = false,
  multiselectOptions,
  selectedOptionIds,
  choicegridOptions,
  choicegridAnswers,
  thinkingSteps,
  thinkingSeconds,
  onSelectOption,
  onConfirmSelection,
  onConfirmChoices,
  onRegenerate,
}: MessageAgentProps) => {
  const hasOptions = (options?.length ?? 0) > 0;
  const hasMultiselect = (multiselectOptions?.length ?? 0) > 0;
  const hasChoicegrid = (choicegridOptions?.length ?? 0) > 0;
  const hasFiles = (generatedFiles?.length ?? 0) > 0;
  // A legacy DDB ref can carry no `file_type`; a type-less file is a document.
  const imageFiles = generatedFiles?.filter((f) => f.file_type?.startsWith('image/')) ?? [];
  const documentFiles = generatedFiles?.filter((f) => !f.file_type?.startsWith('image/')) ?? [];
  // Images placed in the text via `file:` markers render inline; the rest fall back to the end of the message.
  const referenced = new Set(extractFileMarkers(content));
  const trailingImages = imageFiles.filter((f) => !referenced.has(f.file_name));
  const [expanded, setExpanded] = useState<{ file: GeneratedFile; url: string } | null>(null);
  // Kept mounted while closing so the dialog's exit animation can play.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const handleExpand = useCallback((file: GeneratedFile, url: string) => {
    setExpanded({ file, url });
    setLightboxOpen(true);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: AGENT_DURATIONS.fast, ease: AGENT_EASING.outExpo }}
      className="group">
      {thinkingSteps && thinkingSteps.length > 0 && <ThinkingSummary steps={thinkingSteps} seconds={thinkingSeconds} />}

      <MarkdownMessage
        content={content}
        generatedFiles={isStreaming ? undefined : generatedFiles}
        conversationId={conversationId}
        interactionTimestamp={interactionTimestamp}
        onExpand={handleExpand}
      />

      {hasOptions && !isStreaming && (
        <OptionCards
          options={options ?? []}
          selectedId={selectedOptionId}
          disabled={optionsDisabled}
          onSelect={onSelectOption}
        />
      )}

      {hasMultiselect && !isStreaming && (
        <MultiSelectCards
          options={multiselectOptions ?? []}
          selectedIds={selectedOptionIds}
          disabled={optionsDisabled}
          onConfirm={onConfirmSelection}
        />
      )}

      {hasChoicegrid && !isStreaming && (
        <ChoiceGrid
          rows={choicegridOptions ?? []}
          answers={choicegridAnswers}
          disabled={optionsDisabled}
          onConfirm={onConfirmChoices}
        />
      )}

      {hasFiles && !isStreaming && (
        <>
          {trailingImages.length > 0 && (
            <div className={`${hasOptions ? 'mt-3 ' : ''}mb-2 flex flex-col gap-2`}>
              {trailingImages.map((file, idx) => (
                <ImageAttachment
                  key={file.file_name}
                  file={file}
                  conversationId={conversationId}
                  timestamp={interactionTimestamp}
                  index={idx}
                  staggerOffset={(options?.length ?? 0) * 0.08}
                  onExpand={handleExpand}
                />
              ))}
            </div>
          )}
          {documentFiles.length > 0 && (
            <div className={`${hasOptions && trailingImages.length === 0 ? 'mt-3 ' : ''}mb-2 flex flex-wrap gap-2`}>
              {documentFiles.map((file, idx) => (
                <FileDownloadButton
                  key={file.file_name}
                  file={file}
                  conversationId={conversationId}
                  timestamp={interactionTimestamp}
                  index={trailingImages.length + idx}
                  staggerOffset={(options?.length ?? 0) * 0.08}
                />
              ))}
            </div>
          )}
        </>
      )}

      {expanded && (
        <ImageLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          file={expanded.file}
          url={expanded.url}
          conversationId={conversationId}
          timestamp={interactionTimestamp}
        />
      )}

      {!isStreaming && (
        <MessageActions
          content={content}
          timestamp={timestamp}
          conversationId={conversationId}
          interactionTimestamp={interactionTimestamp}
          feedback={feedback}
          isLatest={isLatest}
          onRegenerate={onRegenerate}
        />
      )}
    </motion.div>
  );
};

export default memo(MessageAgent);
